import { ReactNode, RefObject } from 'react';
import { Modal, PrimaryButton, Spinner, StatusBar, Typography } from '@weareopenr/component-library';
import chefPlating from 'src/assets/images/chef-plating.png';
import CollapsibleSection from 'src/components/CollapsibleSection';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { IValidationReportSection } from './types';
import styles from './ValidationReportModal.module.scss';

const SectionErrorList = ({ errors }: { errors: string[] }) =>
  errors.length === 0 ? null : (
    <ul className={styles['error-list']}>
      {errors.map((error, index) => (
        <li key={`${index}-${error}`}>
          <Typography size="small">{error}</Typography>
        </li>
      ))}
    </ul>
  );

const TITLE = 'Validation Report';
const ERROR_MESSAGE = 'Failed to load validation report.';
const NO_RESULTS_MESSAGE = 'No items match the selected filters.';

const ReportSectionRow = ({ section }: { section: IValidationReportSection }) => {
  const errorCount = section.errors.length;

  return (
    <li className={styles['report-item']}>
      <div className={styles['report-item-row']}>
        <div className={styles['report-item-collapsible']}>
          <CollapsibleSection
            title={
              <>
                <strong>{section.title}</strong> ({errorCount} {pluralize('Issue', errorCount)})
              </>
            }
            subtitle={section.subtitle}
            size="small"
            arrowPosition="right"
            arrowColour="purple"
            compact
            scrollable={false}
          >
            <>
              <SectionErrorList errors={section.errors} />
              {section.extra}
            </>
          </CollapsibleSection>
        </div>
        {section.meta && (
          <Typography size="xsmall" weight="regular" className={styles['report-item-meta']}>
            {section.meta}
          </Typography>
        )}
      </div>
    </li>
  );
};

export interface IValidationReportModalProps {
  isOpen: boolean;
  closeModal: () => void;
  headerExtra?: ReactNode;
  filters?: ReactNode;
  sections: IValidationReportSection[];
  isLoading: boolean;
  isError: boolean;
  footerExtra?: ReactNode;
  containerRef?: RefObject<HTMLDivElement>;
  isFetchingNextPage?: boolean;
}

const Content = ({
  closeModal,
  headerExtra,
  filters,
  sections,
  isLoading,
  isError,
  footerExtra,
  containerRef,
  isFetchingNextPage,
}: Omit<IValidationReportModalProps, 'isOpen'>) => {
  const showSpinner = isLoading && !isFetchingNextPage;
  const showError = !showSpinner && isError;
  const showNoResults = !showSpinner && !showError && sections.length === 0;

  return (
    <div className={styles['content']}>
      <div className={styles['header']}>
        <Typography size="xxlarge" weight="bold">
          {TITLE}
        </Typography>
        {headerExtra && <div className={styles['header-extra']}>{headerExtra}</div>}
        {filters && <div className={styles['filters']}>{filters}</div>}
      </div>

      <div className={styles['list-container']} ref={containerRef}>
        {showSpinner && (
          <div className={styles['spinner']}>
            <Spinner size="medium" colour="rainbow" />
          </div>
        )}
        {showError && <StatusBar message={ERROR_MESSAGE} variant="error" />}
        {showNoResults && (
          <div className={styles['no-results']}>
            <Typography>{NO_RESULTS_MESSAGE}</Typography>
          </div>
        )}
        {!showSpinner && !showError && !showNoResults && (
          <ul className={styles['report-list']}>
            {sections.map((section) => (
              <ReportSectionRow key={section.key} section={section} />
            ))}
          </ul>
        )}
        {isFetchingNextPage && (
          <div className={styles['spinner']}>
            <Spinner size="small" colour="rainbow" />
          </div>
        )}
      </div>

      <div className={styles['footer']}>
        {footerExtra}
        <PrimaryButton size="full-width" onClick={closeModal}>
          Close Report
        </PrimaryButton>
      </div>
    </div>
  );
};

export const ValidationReportModal = ({ isOpen, closeModal, ...contentProps }: IValidationReportModalProps) => (
  <Modal open={isOpen} image={chefPlating} imageAlt="Picture of chef at the plating station" closeModal={closeModal}>
    <Content closeModal={closeModal} {...contentProps} />
  </Modal>
);
